<template>
  <base-dialog @cancel="cancelDialog">
    <div slot="header">{{ isUpdate ? $t('common_105') : $t('common_117') }}</div>
    <div slot="body">
      <a-alert v-if="!isUpdate" type="info" :message="$t('system.pre_defined_tag_create_alert')" />
      <a-tabs
        v-if="!isUpdate"
        v-model="tagAddMode"
        class="pre-defined-tag-add-tabs"
        :animated="false"
        @change="handleTagAddModeChange">
        <a-tab-pane key="import" :tab="$t('system.pre_defined_tag_import_from_resource')">
          <tag-select
            class="mt-1 mb-1"
            global
            :button-text="$t('system.pre_defined_tag_select_resource')"
            v-model="checked"
            @input="handleSelectInput"
            :params="params.params || {}"
            :managerInstance="params.managerInstance"
            :allowNoValue="false"
            allowSelectAllValue
            showSelectAll
            multiple
            :isUpdate="isUpdate" />
        </a-tab-pane>
        <a-tab-pane key="create" :tab="$t('system.pre_defined_tag_create_new')" />
      </a-tabs>
      <div v-if="showTagCreateForm">
        <a-form layout="inline" :form="form.fc">
          <a-form-item>
            <a-auto-complete
              :disabled="isUpdate"
              v-decorator="decorators.key"
              :placeholder="$t('common_112')"
              option-label-prop="value"
              dropdown-class-name="pre-defined-tag-key-dropdown"
              @search="handleKeySearch"
              @select="handleKeySelect">
              <template slot="dataSource">
                <a-select-option
                  v-for="name in displayedKeyOptions"
                  :key="name"
                  :value="name">
                  {{ name }}
                </a-select-option>
                <a-select-option
                  v-if="showKeyLoadMore"
                  :key="KEY_LOAD_MORE_SENTINEL"
                  :value="KEY_LOAD_MORE_SENTINEL"
                  class="tag-load-more-option">
                  <div class="tag-load-more-wrap" @mousedown.prevent="handleKeyLoadMore">
                    <a-button type="link" class="tag-load-more-btn">{{ $t('common.LoadMore') }}</a-button>
                  </div>
                </a-select-option>
              </template>
            </a-auto-complete>
          </a-form-item>
          <a-form-item>
            <a-auto-complete
              v-decorator="decorators.value"
              :placeholder="$t('common_113')"
              option-label-prop="value"
              dropdown-class-name="pre-defined-tag-value-dropdown"
              @search="handleValueSearch"
              @select="handleValueSelect">
              <template slot="dataSource">
                <a-select-option
                  v-for="val in displayedValueOptions"
                  :key="val"
                  :value="val"
                  disabled
                  class="tag-value-exists-option">
                  <a-tooltip :title="$t('system.pre_defined_tag_value_exists')">
                    <span class="tag-value-exists-text">{{ val }}</span>
                  </a-tooltip>
                </a-select-option>
                <a-select-option
                  v-if="showValueLoadMore"
                  :key="VALUE_LOAD_MORE_SENTINEL"
                  :value="VALUE_LOAD_MORE_SENTINEL"
                  class="tag-load-more-option">
                  <div class="tag-load-more-wrap" @mousedown.prevent="handleValueLoadMore">
                    <a-button type="link" class="tag-load-more-btn">{{ $t('common.LoadMore') }}</a-button>
                  </div>
                </a-select-option>
              </template>
            </a-auto-complete>
          </a-form-item>
          <a-form-item>
            <a-button :disabled="!canAddTag" @click="addTag">{{$t('common_114')}}</a-button>
          </a-form-item>
        </a-form>
      </div>
      <!-- 选中的标签 -->
      <div class="tag-wrap mt-2">
        <div class="tag-list">
          <template v-if="userTags.length <= 0">
            <loader :no-data-text="$t('common_108')" />
          </template>
          <template v-else>
            <template v-for="item of userTags">
              <a-popover
                trigger="click"
                :visible="getCheckedVisible(item)"
                :key="`${item.key}${item.value}`"
                destroyTooltipOnHide
                @visibleChange="visible => handleTagVisibleChange(item, visible)">
                <template #content>
                  <div class="tag-update-wrap">
                    <div class="mb-1">{{ $t('common_112') }}</div>
                    <div>
                      <div>
                        <a-input size="small" v-model="checkedInfo[getCompositeKey(item.key, item.value)].title" />
                      </div>
                      <template v-if="checkedInfo[getCompositeKey(item.key, item.value)].titleErrorMessage">
                        <div class="error-color mt-1">{{ checkedInfo[getCompositeKey(item.key, item.value)].titleErrorMessage }}</div>
                      </template>
                    </div>
                    <div class="mt-2 mb-1">{{ $t('common_113') }}</div>
                    <div>
                      <a-input size="small" v-model="checkedInfo[getCompositeKey(item.key, item.value)].value" />
                    </div>
                    <a-row :gutter="8" class="mt-2">
                      <a-col :span="12">
                        <a-button size="small" block @click="updateTag(item)">{{ $t('common.ok') }}</a-button>
                      </a-col>
                      <a-col :span="12">
                        <a-button
                          size="small"
                          block
                          @click="() => handleTagPopoverCancel(item)">{{ $t('common.cancel') }}</a-button>
                      </a-col>
                    </a-row>
                  </div>
                </template>
                <div
                  class="tag mb-1 d-inline-block"
                  :title="item.title"
                  :style="{ backgroundColor: item.backgroundColor, color: item.color, borderColor: item.color }">
                  <div class="d-flex align-items-center">
                    <a-tooltip v-if="isSysTag(item.key)">
                      <template slot="title">{{ $t('common.tag.sys_tag_tooltip') }}</template>
                      <span class="flex-fill text-truncate">{{ item.title }}</span>
                    </a-tooltip>
                    <span v-else class="flex-fill text-truncate">{{ item.title }}</span>
                    <a-icon
                      v-if="!isSysTag(item.key)"
                      class="ml-1 remove-tag flex-grow-0 flex-shrink-0"
                      type="close"
                      @click.stop="removeTag(item)" />
                  </div>
                </div>
              </a-popover>
            </template>
          </template>
        </div>
      </div>
    </div>
    <div slot="footer">
      <a-button type="primary" @click="handleConfirm" :loading="loading">{{ $t('dialog.ok') }}</a-button>
      <a-button @click="cancelDialog">{{ $t('dialog.cancel') }}</a-button>
    </div>
  </base-dialog>
</template>

<script>
import * as R from 'ramda'
import { getTagColor, getTagTitle, isSysTag } from '@/utils/common/tag'
import DialogMixin from '@/mixins/dialog'
import WindowsMixin from '@/mixins/windows'
import TagSelect from '@/sections/TagSelect'

const KEY_OPTION_PAGE_SIZE = 10
const KEY_LOAD_MORE_SENTINEL = '__key_load_more__'
const VALUE_LOAD_MORE_SENTINEL = '__value_load_more__'

export default {
  name: 'PreDefinedTagCreateDialog',
  components: {
    TagSelect,
  },
  mixins: [DialogMixin, WindowsMixin],
  data () {
    const { data = [], type } = this.params
    const checked = type === 'update' ? { [data[0].name]: data[0].values } : {}
    // 根据初始 checked 预生成一份 checkedInfo，避免模板直接访问 undefined.title / undefined.value
    const checkedInfo = {}
    let checkedIndex = 0
    R.forEachObjIndexed((value, key) => {
      const vals = Array.isArray(value) ? value : (value ? [value] : [])
      const index = ++checkedIndex
      if (vals.length === 0) {
        const compositeKey = `${key}\x01`
        checkedInfo[compositeKey] = {
          visible: false,
          title: getTagTitle(key),
          value: '',
          titleErrorMessage: '',
          index,
        }
      } else {
        vals.forEach(v => {
          const compositeKey = `${key}\x01${v ?? ''}`
          checkedInfo[compositeKey] = {
            visible: false,
            title: getTagTitle(key, v),
            value: v,
            titleErrorMessage: '',
            index,
          }
        })
      }
    }, checked)
    return {
      loading: false,
      isUpdate: type === 'update',
      form: {
        fc: this.$form.createForm(this),
      },
      decorators: {
        key: [
          'key',
          {
            initialValue: type === 'update' ? data[0].name : '',
            validateTrigger: [],
            rules: [
              { required: true, whitespace: true, message: this.$t('common_116') },
            ],
          },
        ],
        value: [
          'value',
          {
            validateTrigger: [],
            rules: [
              { required: true, whitespace: true, message: this.$t('common.tips.input', [this.$t('common_113')]) },
            ],
          },
        ],
      },
      extTags: [],
      checked,
      checkedInfo,
      checkedIndex,
      tagAddMode: 'import',
      allTags: [],
      allTagNames: [],
      filteredTagNames: [],
      filteredTagValues: [],
      keyOptionDisplayCount: KEY_OPTION_PAGE_SIZE,
      valueOptionDisplayCount: KEY_OPTION_PAGE_SIZE,
      formKeyInput: '',
      formValueInput: '',
      KEY_LOAD_MORE_SENTINEL,
      VALUE_LOAD_MORE_SENTINEL,
    }
  },
  computed: {
    mode () {
      return this.params.mode || 'default'
    },
    isDefaultMode () {
      return this.mode === 'default'
    },
    isBatchAddMode () {
      return this.isBatch && this.mode === 'add'
    },
    userTagTitle () {
      return this.$t('common_118')
    },
    isImportMode () {
      return !this.isUpdate && this.tagAddMode === 'import'
    },
    showTagCreateForm () {
      return this.isUpdate || this.tagAddMode === 'create'
    },
    userTags () {
      let ret = []
      R.forEachObjIndexed((value, key) => {
        if (value.length > 0) {
          for (let i = 0, len = value.length; i < len; i++) {
            const compositeKey = this.getCompositeKey(key, value[i])
            const info = this.checkedInfo[compositeKey]
            ret.push(this.genTag({ key, value: value[i], index: info ? info.index : 0 }))
          }
        } else {
          const compositeKey = this.getCompositeKey(key, null)
          const info = this.checkedInfo[compositeKey]
          ret.push(this.genTag({ key, value: null, index: info ? info.index : 0 }))
        }
      }, this.checked)
      ret = R.sort((a, b) => a.index - b.index, ret)
      return ret
    },
    displayedKeyOptions () {
      return this.filteredTagNames.slice(0, this.keyOptionDisplayCount)
    },
    showKeyLoadMore () {
      return this.filteredTagNames.length > this.keyOptionDisplayCount
    },
    displayedValueOptions () {
      return this.filteredTagValues.slice(0, this.valueOptionDisplayCount)
    },
    showValueLoadMore () {
      return this.filteredTagValues.length > this.valueOptionDisplayCount
    },
    canAddTag () {
      const key = R.trim(this.formKeyInput || '')
      const value = R.trim(this.formValueInput || '')
      if (!key || !value) return false
      return !this.isTagValueExists(key, value)
    },
  },
  created () {
    this.decorators.value[1].rules.push({
      validator: this.validateTagValueDuplicate,
    })
    this.fetchTagNames()
  },
  methods: {
    handleTagAddModeChange () {
      this.checked = {}
      this.checkedInfo = {}
      this.checkedIndex = 0
      this.resetTagForm()
    },
    normalizeTagKey (key) {
      return R.trim(key || '').replace(/^user:/, '')
    },
    getTagValuesByKey (key) {
      const normalizedKey = this.normalizeTagKey(key)
      if (!normalizedKey) return []
      if (this.isUpdate) {
        const checkedKey = Object.keys(this.checked).find(k => this.normalizeTagKey(k) === normalizedKey)
        return checkedKey ? (this.checked[checkedKey] || []) : []
      }
      const tag = this.allTags.find(item => item.name === normalizedKey)
      return tag ? (tag.values || []) : []
    },
    isTagValueExists (key, value) {
      const normalizedKey = this.normalizeTagKey(key)
      const normalizedValue = R.trim(value || '')
      if (!normalizedKey || !normalizedValue) return false
      const checkedKey = Object.keys(this.checked).find(k => this.normalizeTagKey(k) === normalizedKey)
      if (checkedKey && (this.checked[checkedKey] || []).includes(normalizedValue)) return true
      if (this.isUpdate) return false
      const tag = this.allTags.find(item => item.name === normalizedKey)
      return !!(tag && (tag.values || []).includes(normalizedValue))
    },
    validateTagValueDuplicate (rule, value, callback) {
      const key = this.form.fc.getFieldValue('key')
      if (this.isTagValueExists(key, value)) {
        callback(new Error(this.$t('system.pre_defined_tag_value_exists')))
        return
      }
      callback()
    },
    updateValueOptions (key, valueKeyword = '') {
      const values = this.getTagValuesByKey(key)
      const keyword = R.trim(valueKeyword || '').toLowerCase()
      this.filteredTagValues = keyword
        ? values.filter(val => String(val).toLowerCase().includes(keyword))
        : values
      this.valueOptionDisplayCount = KEY_OPTION_PAGE_SIZE
    },
    revalidateValueField () {
      this.$nextTick(() => {
        const val = this.form.fc.getFieldValue('value')
        if (val) {
          this.form.fc.validateFields(['value'], { force: true })
        } else {
          this.form.fc.setFields({
            value: {
              value: val,
              errors: null,
            },
          })
        }
      })
    },
    async fetchTagNames () {
      try {
        const manager = new this.$Manager('tags')
        const { data } = await manager.list({ params: { limit: 1000 } })
        this.allTags = data.data || []
        this.allTagNames = this.allTags.map(item => item.name)
        this.resetKeyOptions()
        if (this.isUpdate) {
          const key = this.params.data[0].name
          this.formKeyInput = key
          this.updateValueOptions(key)
        }
      } catch (error) {
        console.error(error)
      }
    },
    resetValueOptions () {
      this.filteredTagValues = []
      this.valueOptionDisplayCount = KEY_OPTION_PAGE_SIZE
    },
    resetTagFormOptions () {
      this.resetKeyOptions()
      this.resetValueOptions()
      this.formKeyInput = ''
      this.formValueInput = ''
    },
    resetTagForm () {
      if (this.isUpdate) {
        const key = this.params.data[0].name
        this.form.fc.setFieldsValue({ key, value: '' })
        this.formKeyInput = key
        this.formValueInput = ''
        this.updateValueOptions(key)
        this.$nextTick(() => {
          this.form.fc.setFields({
            key: { value: key, errors: null },
            value: { value: '', errors: null },
          })
        })
        return
      }
      this.form.fc.resetFields()
      this.resetTagFormOptions()
      this.$nextTick(() => {
        this.form.fc.setFields({
          key: { errors: null },
          value: { errors: null },
        })
      })
    },
    resetKeyOptions () {
      this.filteredTagNames = this.allTagNames
      this.keyOptionDisplayCount = KEY_OPTION_PAGE_SIZE
    },
    handleKeySearch (value) {
      this.formKeyInput = value
      const keyword = (value || '').trim().toLowerCase()
      this.filteredTagNames = keyword
        ? this.allTagNames.filter(name => name.toLowerCase().includes(keyword))
        : this.allTagNames
      this.keyOptionDisplayCount = KEY_OPTION_PAGE_SIZE
      this.updateValueOptions(value, this.formValueInput)
      this.revalidateValueField()
    },
    handleKeyLoadMore () {
      const menu = this.getKeyDropdownMenu()
      const scrollTop = menu ? menu.scrollTop : 0
      this.keyOptionDisplayCount += KEY_OPTION_PAGE_SIZE
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const nextMenu = this.getKeyDropdownMenu()
          if (nextMenu) {
            nextMenu.scrollTop = scrollTop
          }
        })
      })
    },
    getKeyDropdownMenu () {
      const dropdown = document.querySelector('.pre-defined-tag-key-dropdown')
      return dropdown ? dropdown.querySelector('.ant-select-dropdown-menu') : null
    },
    handleKeySelect (value) {
      if (value === KEY_LOAD_MORE_SENTINEL) return
      this.formKeyInput = value
      this.form.fc.setFieldsValue({ key: value })
      this.updateValueOptions(value, this.formValueInput)
      this.revalidateValueField()
    },
    handleValueSearch (value) {
      this.formValueInput = value
      const key = this.form.fc.getFieldValue('key') || this.formKeyInput
      this.updateValueOptions(key, value)
      this.revalidateValueField()
    },
    handleValueLoadMore () {
      const menu = this.getValueDropdownMenu()
      const scrollTop = menu ? menu.scrollTop : 0
      this.valueOptionDisplayCount += KEY_OPTION_PAGE_SIZE
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const nextMenu = this.getValueDropdownMenu()
          if (nextMenu) {
            nextMenu.scrollTop = scrollTop
          }
        })
      })
    },
    getValueDropdownMenu () {
      const dropdown = document.querySelector('.pre-defined-tag-value-dropdown')
      return dropdown ? dropdown.querySelector('.ant-select-dropdown-menu') : null
    },
    handleValueSelect (value) {
      if (value === VALUE_LOAD_MORE_SENTINEL) return
      this.formValueInput = value
      this.form.fc.setFieldsValue({ value })
      this.revalidateValueField()
    },
    getCompositeKey (key, value) {
      return `${key}\x01${value ?? ''}`
    },
    getCheckedVisible (item) {
      const compositeKey = this.getCompositeKey(item.key, item.value)
      return (this.checkedInfo[compositeKey] && this.checkedInfo[compositeKey].visible) || false
    },
    async addTag () {
      if (!this.canAddTag) return
      try {
        const values = await this.form.fc.validateFields()
        const key = this.isUpdate
          ? this.normalizeTagKey(values.key)
          : (values.key.startsWith('user:') ? values.key : `user:${values.key}`)
        const tagValue = R.trim(values.value)
        const newValue = { ...this.checked }
        const newCheckedInfo = { ...this.checkedInfo }
        if (newValue[key]) {
          if (!newValue[key].includes(tagValue)) {
            newValue[key] = [...newValue[key], tagValue]
            const compositeKey = this.getCompositeKey(key, tagValue)
            const firstCompositeKey = this.getCompositeKey(key, newValue[key][0])
            const index = newCheckedInfo[firstCompositeKey] ? newCheckedInfo[firstCompositeKey].index : ++this.checkedIndex
            newCheckedInfo[compositeKey] = {
              visible: false,
              title: getTagTitle(key),
              value: tagValue,
              titleErrorMessage: '',
              index,
            }
          }
        } else {
          newValue[key] = [tagValue]
          newCheckedInfo[this.getCompositeKey(key, tagValue)] = {
            visible: false,
            title: getTagTitle(key),
            value: tagValue,
            titleErrorMessage: '',
            index: ++this.checkedIndex,
          }
        }
        this.checked = newValue
        this.checkedInfo = newCheckedInfo
        this.resetTagForm()
      } catch (error) {
        throw error
      }
    },
    removeTag (item) {
      const newValue = { ...this.checked }
      const newCheckedVisible = { ...this.checkedInfo }
      const compositeKey = this.getCompositeKey(item.key, item.value)
      const arr = (newValue[item.key] || []).filter(v => v !== item.value)
      if (arr.length === 0) {
        delete newValue[item.key]
      } else {
        newValue[item.key] = arr
      }
      delete newCheckedVisible[compositeKey]
      this.checked = newValue
      this.checkedInfo = newCheckedVisible
      if (this.isUpdate) {
        const key = this.params.data[0].name
        this.updateValueOptions(key, this.formValueInput)
      }
    },
    async handleConfirm () {
      this.loading = true
      try {
        const data = {}
        R.forEachObjIndexed((value, key) => {
          const vals = Array.isArray(value) ? value : (value ? [value] : [])
          // 更新场景：后端已有 name，直接按当前 checked 里的 key/values 提交
          if (this.isUpdate) {
            const _key = key.startsWith('user:') ? R.replace(/^user:/, '', key) : key
            data[_key] = vals
            return
          }
          // 创建场景：只处理 user: 前缀的标签，并去掉前缀作为 name
          if (key.startsWith('user:')) {
            const _key = R.replace(/^user:/, '', key)
            data[_key] = vals
          }
        }, this.checked)
        const { onManager } = this.params
        const keys = Object.keys(data)
        if (this.isUpdate) {
          await onManager('update', { managerArgs: { id: this.params.data[0].id, data: { name: keys[0], values: data[keys[0]] } } })
          this.cancelDialog()
          R.is(Function, this.params.refresh) && this.params.refresh()
          return
        }
        const tags = keys.map(name => ({ name, values: data[name] }))
        if (tags.length === 0) {
          this.cancelDialog()
          return
        }
        await new this.$Manager('tags').performClassAction({
          action: 'batch-import',
          data: { tags },
        })
        this.$message.success(this.$t('system.pre_defined_tag_create_success', [tags.length]))
        this.cancelDialog()
        if (R.is(Function, this.params.refresh)) {
          this.params.refresh()
        } else if (this.params.vm?.list) {
          this.params.vm.list.fetchData()
        }
      } catch (error) {
        throw error
      } finally {
        this.loading = false
      }
    },
    genTag (item) {
      const rgb = getTagColor(item.key, item.value, 'rgb')
      const strRgb = rgb.join(',')
      return {
        title: getTagTitle(item.key, item.value),
        color: `rgb(${strRgb})`,
        backgroundColor: `rgba(${strRgb},.1)`,
        ...item,
      }
    },
    updateTag (item) {
      const compositeKey = this.getCompositeKey(item.key, item.value)
      const info = this.checkedInfo[compositeKey]
      if (!info) return
      const title = R.trim(info.title)
      const index = info.index
      if (R.isEmpty(title)) {
        info.titleErrorMessage = this.$t('common_116')
        return
      }
      const value = R.trim(info.value || '')
      const newKey = title
      const newCheckedInfo = { ...this.checkedInfo }
      delete newCheckedInfo[compositeKey]
      const newCheckedValues = { ...this.checked }
      const arr = (newCheckedValues[item.key] || []).filter(v => v !== item.value)
      if (arr.length === 0) {
        delete newCheckedValues[item.key]
      } else {
        newCheckedValues[item.key] = arr
      }
      if (newKey === item.key) {
        newCheckedValues[item.key] = [...(newCheckedValues[item.key] || []), value]
        newCheckedInfo[this.getCompositeKey(item.key, value)] = {
          visible: false,
          title: getTagTitle(item.key),
          value,
          titleErrorMessage: '',
          index,
        }
      } else {
        newCheckedValues[newKey] = [...(newCheckedValues[newKey] || []), value]
        newCheckedInfo[this.getCompositeKey(newKey, value)] = {
          visible: false,
          title: getTagTitle(newKey),
          value,
          titleErrorMessage: '',
          index,
        }
      }
      this.checkedInfo = newCheckedInfo
      this.checked = newCheckedValues
    },
    handleSelectInput (newChecked) {
      const newCheckedInfo = { ...this.checkedInfo }
      R.forEachObjIndexed((value, key) => {
        const vals = Array.isArray(value) ? value : (value ? [value] : [])
        const index = ++this.checkedIndex
        // 只选 key（allowNoValue）时，vals 可能为空，需要补一份 checkedInfo，避免模板访问 undefined
        if (vals.length === 0) {
          const compositeKey = this.getCompositeKey(key, null)
          newCheckedInfo[compositeKey] = {
            visible: false,
            title: getTagTitle(key),
            value: '',
            titleErrorMessage: '',
            index,
          }
          return
        }
        vals.forEach((v, i) => {
          const compositeKey = this.getCompositeKey(key, v)
          newCheckedInfo[compositeKey] = {
            visible: false,
            title: getTagTitle(key),
            value: v,
            titleErrorMessage: '',
            index,
          }
        })
      }, newChecked)
      this.checkedInfo = newCheckedInfo
      this.checked = newChecked
    },
    handleTagVisibleChange (item, visible) {
      const compositeKey = this.getCompositeKey(item.key, item.value)
      let _visable = visible
      if (isSysTag(item.key)) {
        if (this.checkedInfo[compositeKey]) this.checkedInfo[compositeKey].visible = false
        _visable = false
        return
      }
      // 打开/关闭都确保有对应的 checkedInfo，避免 :visible / 表单绑定读取时为空
      if (!this.checkedInfo[compositeKey]) {
        const newCheckedInfo = { ...this.checkedInfo }
        newCheckedInfo[compositeKey] = {
          visible: !!_visable,
          title: getTagTitle(item.key),
          value: item.value ?? '',
          titleErrorMessage: '',
          index: item.index,
        }
        this.checkedInfo = newCheckedInfo
      } else {
        this.checkedInfo[compositeKey].visible = !!_visable
      }
      if (!_visable && this.checkedInfo[compositeKey]) {
        const newCheckedInfo = { ...this.checkedInfo }
        newCheckedInfo[compositeKey] = {
          visible: false,
          title: getTagTitle(item.key),
          value: item.value,
          titleErrorMessage: '',
          index: item.index,
        }
        this.checkedInfo = newCheckedInfo
      }
    },
    handleTagPopoverCancel (item) {
      const compositeKey = this.getCompositeKey(item.key, item.value)
      if (this.checkedInfo[compositeKey]) this.checkedInfo[compositeKey].visible = false
      this.handleTagVisibleChange(item, false)
    },
    isSysTag,
  },
}
</script>

<style lang="less" scoped>
@import '@/styles/less/theme.less';
.tag {
  max-width: 100%;
  line-height: 20px;
  margin-right: 10px;
  padding: 0 6px 0 4px;
  font-size: 12px;
  border-style: solid;
  border-width: 1px;
}
.tag-list {
  min-height: 96px;
  border: 2px dashed #ddd;
  padding: 8px;
  .wrap {
    margin: 0;
  }
  & ::v-deep .ant-empty-image {
    height: 46px;
    .data-empty {
      margin-top: 0;
    }
  }
}
.remove-tag {
  font-size: 12px;
  cursor: pointer;
}
.tag-update-wrap {
  width: 200px;
}
.pre-defined-tag-add-tabs {
  overflow: visible;
}
</style>

<style lang="less">
.pre-defined-tag-key-dropdown,
.pre-defined-tag-value-dropdown {
  .tag-value-exists-option.ant-select-dropdown-menu-item {
    cursor: not-allowed;
    &.ant-select-dropdown-menu-item-disabled {
      color: rgba(0, 0, 0, 0.25);
      &:hover {
        background-color: transparent;
      }
    }
  }
  .tag-value-exists-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: rgba(0, 0, 0, 0.25);
  }
  .tag-load-more-option.ant-select-dropdown-menu-item {
    padding: 12px 16px;
    text-align: center;
    &:hover,
    &.ant-select-dropdown-menu-item-active {
      background-color: transparent;
    }
  }
  .tag-load-more-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4px 0;
  }
  .tag-load-more-btn {
    height: auto;
    padding: 0;
    line-height: 22px;
  }
}
</style>
